import { ExternalLink } from "lucide-react";
import { cn } from "@repo/ui/lib/utils";

interface DiscoveryUrlBubbleProps {
  url: string | null;
  className?: string;
}

function parseUrl(raw: string): { host: string; pathPlusQuery: string } | null {
  try {
    const u = new URL(raw);
    const host = u.host.replace(/^www\./i, "");
    const pathPlusQuery = `${u.pathname}${u.search}` || "/";
    return { host, pathPlusQuery };
  } catch {
    return null;
  }
}

const HOST_TAIL_CHARS = 10;

function shortenHost(host: string): string {
  if (host.length <= HOST_TAIL_CHARS) return host;
  return `…${host.slice(-(HOST_TAIL_CHARS - 1))}`;
}

export function DiscoveryUrlBubble({ url, className }: DiscoveryUrlBubbleProps) {
  if (!url) return null;
  const parsed = parseUrl(url);
  if (!parsed) return null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title={url}
      aria-label={url}
      className={cn(
        "group flex items-center gap-1 min-w-0 w-full overflow-hidden no-underline",
        className,
      )}
    >
      <span
        title={url}
        className={cn(
          "shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-sm",
          "text-[10px] font-mono uppercase tracking-tight",
          "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
        )}
      >
        {shortenHost(parsed.host)}
      </span>
      <span
        title={url}
        className={cn(
          "min-w-0 flex-1 truncate",
          "text-xs font-mono leading-tight",
          "bg-primary/5 text-foreground px-2 py-0.5 rounded-sm",
          "group-hover:bg-primary/10 group-hover:text-primary transition-colors",
        )}
      >
        {parsed.pathPlusQuery}
      </span>
      <ExternalLink className="w-3 h-3 shrink-0 text-muted-foreground group-hover:text-primary" />
    </a>
  );
}
